import * as React from 'react';
import { ComponentProps, ReactNode } from 'react';
import { MathJax } from 'better-react-mathjax';
import parse from 'html-react-parser';

type ParsedMathJaxProps = Omit<ComponentProps<typeof MathJax>, 'children'> & {
  content: string;
};

export function ParsedMathJax({ content, ...mathJaxProps }: ParsedMathJaxProps): ReactNode {
  return (
    <MathJax {...mathJaxProps}>
      {parse(parseBoldsymbol(content))}
    </MathJax>
  );
}

/**
 * As \boldsymbol isn't rendered correctly in MathJax, each occurrence within MathJax entries gets replaced by \pmb.
 * Replacements are limited to MathJax-delimited sections so non-math HTML/text content is left unchanged.
 *
 * @param content The HTML/text content that may contain MathJax expressions.
 */
function parseBoldsymbol(content: string): string {
  const mathDelimiters = [
    {start: '$$', end: '$$'},
    {start: '\\[', end: '\\]'},
    {start: '\\(', end: '\\)'},
    {start: '$', end: '$'},
  ];

  const hasOddNumberOfBackslashesBefore = (index: number): boolean => {
    let backslashCount = 0;

    for (let i = index - 1; i >= 0 && content[i] === '\\'; i--) {
      backslashCount++;
    }

    return backslashCount % 2 === 1;
  };

  let parsedContent = '';
  let currentIndex = 0;

  while (currentIndex < content.length) {
    let nextStartIndex = -1;
    let matchingDelimiter = mathDelimiters[0];

    for (const delimiter of mathDelimiters) {
      let delimiterStartIndex = content.indexOf(delimiter.start, currentIndex);

      while (
        delimiterStartIndex !== -1 &&
        delimiter.start === '$' &&
        hasOddNumberOfBackslashesBefore(delimiterStartIndex)
      ) {
        delimiterStartIndex = content.indexOf(delimiter.start, delimiterStartIndex + delimiter.start.length);
      }

      if (delimiterStartIndex !== -1 && (nextStartIndex === -1 || delimiterStartIndex < nextStartIndex)) {
        nextStartIndex = delimiterStartIndex;
        matchingDelimiter = delimiter;
      }
    }

    if (nextStartIndex === -1) {
      parsedContent += content.substring(currentIndex);
      break;
    }

    const mathEndIndex = content.indexOf(
      matchingDelimiter.end,
      nextStartIndex + matchingDelimiter.start.length,
    );

    if (mathEndIndex === -1) {
      parsedContent += content.substring(currentIndex);
      break;
    }

    parsedContent += content.substring(currentIndex, nextStartIndex);

    const mathContent = content.substring(
      nextStartIndex,
      mathEndIndex + matchingDelimiter.end.length,
    );
    parsedContent += mathContent.replace(/\\boldsymbol\b/g, '\\pmb');

    currentIndex = mathEndIndex + matchingDelimiter.end.length;
  }

  return parsedContent;
}

export default ParsedMathJax;